import { IBuilderModel, ITransition } from '../models/workflow-builder.model';

export type WorkflowOutcome = 'one' | 'two';

export interface WorkflowOutcomeRef {
  tid: string;
  which: WorkflowOutcome;
}

export interface WorkflowVisualNode {
  key: string;
  stateCode: string;
  ref: WorkflowOutcomeRef | null;
  occurrenceIndex: number;
  occurrenceCount: number;
}

export interface WorkflowVisualEdge {
  id: string;
  tid: string;
  which: WorkflowOutcome;
  sourceKey: string;
  targetKey: string;
}

export interface WorkflowVisualGraph {
  nodes: WorkflowVisualNode[];
  edges: WorkflowVisualEdge[];
}

export const outcomeNodeKey = (tid: string, which: WorkflowOutcome): string =>
  `outcome:${tid}:${which}`;

const outcomesOf = (
  transition: ITransition
): Array<{ which: WorkflowOutcome; stateCode: string }> => [
  { which: 'one', stateCode: transition.one.stateCode },
  ...(transition.two
    ? [{ which: 'two' as const, stateCode: transition.two.stateCode }]
    : []),
];

/**
 * Project the semantic workflow into canvas nodes. Repeated terminal outcomes
 * become path-local cards; every state with outgoing events remains canonical.
 */
export function buildWorkflowVisualGraph(
  model: IBuilderModel
): WorkflowVisualGraph {
  const outgoing = new Set(model.transitions.map(t => t.startState));
  const incoming = new Map<
    string,
    Array<{ tid: string; which: WorkflowOutcome }>
  >();

  model.transitions.forEach(transition => {
    outcomesOf(transition).forEach(outcome => {
      const refs = incoming.get(outcome.stateCode) ?? [];
      refs.push({ tid: transition.id, which: outcome.which });
      incoming.set(outcome.stateCode, refs);
    });
  });

  const expanded = new Set(
    [...incoming.entries()]
      .filter(
        ([stateCode, refs]) => !outgoing.has(stateCode) && refs.length > 1
      )
      .map(([stateCode]) => stateCode)
  );

  const nodes: WorkflowVisualNode[] = [];
  model.states.forEach(state => {
    const refs = incoming.get(state.code) ?? [];
    if (!expanded.has(state.code)) {
      nodes.push({
        key: state.code,
        stateCode: state.code,
        ref: refs[0] ?? null,
        occurrenceIndex: refs.length ? 1 : 0,
        occurrenceCount: refs.length,
      });
      return;
    }

    refs.forEach((ref, index) => {
      nodes.push({
        key: outcomeNodeKey(ref.tid, ref.which),
        stateCode: state.code,
        ref,
        occurrenceIndex: index + 1,
        occurrenceCount: refs.length,
      });
    });
  });

  const targetKeys = new Map<string, string>();
  nodes.forEach(node => {
    if (
      node.ref &&
      node.occurrenceCount > 1 &&
      node.key.startsWith('outcome:')
    ) {
      targetKeys.set(`${node.ref.tid}:${node.ref.which}`, node.key);
    }
  });

  const edges: WorkflowVisualEdge[] = model.transitions.flatMap(transition =>
    outcomesOf(transition).map(outcome => ({
      id: `${transition.id}-${outcome.which}`,
      tid: transition.id,
      which: outcome.which,
      sourceKey: transition.startState,
      targetKey:
        targetKeys.get(`${transition.id}:${outcome.which}`) ??
        outcome.stateCode,
    }))
  );

  return { nodes, edges };
}
